import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Briefcase, MapPin, Buildings, Clock, Eye, ArrowLeft, PencilSimple, Trash, Users, UserPlus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SocialPulseBadge from "@/components/SocialPulseBadge";
import JobFormDialog, { WORK_MODE_LABELS, EMP_TYPE_LABELS } from "@/components/JobFormDialog";

function timeago(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}
function salaryLabel(j) {
  if (!j.salary_min && !j.salary_max) return null;
  const cur = j.salary_currency || "";
  const range = j.salary_min && j.salary_max ? `${j.salary_min.toLocaleString()}–${j.salary_max.toLocaleString()}` : (j.salary_min || j.salary_max).toLocaleString();
  return `${cur} ${range} / ${j.salary_period || "year"}`;
}

const APP_STATUSES = ["submitted", "reviewed", "shortlisted", "rejected"];

function ReferDialog({ jobId, jobTitle, open, onOpenChange }) {
  const [cands, setCands] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (open) api.get(`/jobs/${jobId}/referral-candidates?q=${encodeURIComponent(q)}`).then((r) => setCands(r.data.candidates)).catch(() => setCands([]));
  }, [open, q, jobId]);
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async () => {
    if (sel.size === 0) { toast.error("Pick at least one connection"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/refer`, { user_ids: [...sel], note });
      toast.success(`Referred ${data.sent} connection${data.sent === 1 ? "" : "s"}`);
      onOpenChange(false); setSel(new Set()); setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not refer"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="refer-dialog">
        <DialogHeader><DialogTitle className="font-heading text-lg">Refer a connection to {jobTitle}</DialogTitle></DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your connections" data-testid="refer-search" />
        <div className="max-h-64 space-y-1 overflow-y-auto" data-testid="refer-candidates">
          {cands === null ? <div className="h-16 animate-pulse rounded-lg bg-muted" /> : cands.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No connections to refer. Connect with alumni first.</p>
          ) : cands.map((c) => (
            <label key={c.id} className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 hover:bg-accent/50" data-testid={`refer-cand-${c.id}`}>
              <Checkbox checked={sel.has(c.id)} onCheckedChange={() => toggle(c.id)} />
              <Avatar className="h-8 w-8"><AvatarImage src={c.avatar ? fileUrl(c.avatar) : undefined} /><AvatarFallback>{(c.name || "M")[0]}</AvatarFallback></Avatar>
              <div className="min-w-0"><p className="truncate text-sm font-medium">{c.name}</p>{c.headline && <p className="truncate text-xs text-muted-foreground">{c.headline}</p>}</div>
            </label>
          ))}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a note (optional) — why they'd be a great fit" data-testid="refer-note" />
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full" onClick={send} disabled={sending} data-testid="refer-send">{sending ? "Sending…" : `Refer${sel.size ? ` (${sel.size})` : ""}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplicantsDialog({ jobId, open, onOpenChange }) {
  const [apps, setApps] = useState(null);
  useEffect(() => {
    if (open) api.get(`/jobs/${jobId}/applications`).then((r) => setApps(r.data.applications)).catch(() => setApps([]));
  }, [open, jobId]);
  const setStatus = async (aid, status) => {
    try {
      await api.post(`/jobs/${jobId}/applications/${aid}/status`, { status });
      setApps((prev) => prev.map((a) => a.id === aid ? { ...a, status } : a));
      toast.success("Updated");
    } catch { toast.error("Could not update"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="applicants-dialog">
        <DialogHeader><DialogTitle className="font-heading text-lg">Applicants</DialogTitle></DialogHeader>
        {apps === null ? <div className="h-24 animate-pulse rounded-xl bg-muted" /> : apps.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No applications yet.</p>
        ) : (
          <div className="space-y-3">
            {apps.map((a) => (
              <div key={a.id} className="rounded-xl border border-border/60 p-3" data-testid={`applicant-${a.id}`}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-9 w-9"><AvatarImage src={a.applicant.avatar ? fileUrl(a.applicant.avatar) : undefined} /><AvatarFallback>{(a.applicant.name || "M")[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      {a.applicant.slug ? <Link to={`/u/${a.applicant.slug}`} className="truncate text-sm font-semibold hover:underline">{a.applicant.name}</Link> : <span className="truncate text-sm font-semibold">{a.applicant.name}</span>}
                      <SocialPulseBadge verified={a.applicant.socialpulse_verified} showLabel={false} />
                    </div>
                    {a.applicant.headline && <p className="truncate text-xs text-muted-foreground">{a.applicant.headline}</p>}
                  </div>
                  <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                    <SelectTrigger className="h-8 w-32 shrink-0 text-xs" data-testid={`applicant-status-${a.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{APP_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {a.note && <p className="mt-2 whitespace-pre-wrap rounded-lg bg-muted/50 p-2 text-sm">{a.note}</p>}
                {a.resume_url && <a href={a.resume_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary hover:underline">View resume ↗</a>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [j, setJ] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [note, setNote] = useState("");
  const [resumeUrl, setResumeUrl] = useState("");
  const [applying, setApplying] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showApplicants, setShowApplicants] = useState(false);
  const [showRefer, setShowRefer] = useState(false);

  const load = () => api.get(`/jobs/${id}`).then((r) => setJ(r.data)).catch(() => setJ(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (j === null) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (j === false) return <div className="p-12 text-center text-muted-foreground">Job not found.</div>;

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/jobs/${id}/apply`, { note, resume_url: resumeUrl });
      toast.success("Application sent!");
      setShowApply(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); }
    finally { setApplying(false); }
  };
  const toggleClose = async () => {
    try { const { data } = await api.post(`/jobs/${id}/close`); setJ((p) => ({ ...p, status: data.status })); toast.success(data.status === "closed" ? "Job closed" : "Job reopened"); }
    catch { toast.error("Could not update"); }
  };
  const remove = async () => {
    try { await api.delete(`/jobs/${id}`); toast.success("Job deleted"); nav("/jobs"); }
    catch { toast.error("Could not delete"); }
  };

  const alreadyApplied = !!j.my_application;

  const ApplyAction = () => {
    if (j.am_poster) return null;
    if (j.status !== "open") return <Button disabled className="rounded-full">Closed</Button>;
    if (j.apply_type === "url") return <a href={j.apply_url} target="_blank" rel="noreferrer"><Button className="gap-2 rounded-full" data-testid="apply-external-btn">Apply on website ↗</Button></a>;
    if (j.apply_type === "email") return <a href={`mailto:${j.apply_email}?subject=${encodeURIComponent("Application: " + j.title)}`}><Button className="gap-2 rounded-full" data-testid="apply-email-btn">Apply by email</Button></a>;
    if (alreadyApplied) return <Button disabled className="rounded-full capitalize" data-testid="applied-badge">Applied · {j.my_application.status}</Button>;
    if (!user) return <Link to="/login"><Button className="rounded-full">Sign in to apply</Button></Link>;
    return <Button className="rounded-full" onClick={() => setShowApply(true)} data-testid="apply-btn">Apply now</Button>;
  };

  return (
    <main className="mx-auto max-w-3xl px-5 py-8" data-testid="job-detail">
      <button onClick={() => nav("/jobs")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> All jobs</button>

      <Card className="border-border/60 p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl" data-testid="job-title">{j.title}</h1>
              {j.status === "closed" && <Badge variant="secondary">Closed</Badge>}
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-muted-foreground"><Buildings size={16} /> {j.company}</p>
          </div>
          <ApplyAction />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Badge variant="outline" className="rounded-full font-normal">{EMP_TYPE_LABELS[j.employment_type] || j.employment_type}</Badge>
          <Badge variant="outline" className="rounded-full font-normal">{WORK_MODE_LABELS[j.work_mode] || j.work_mode}</Badge>
          {j.category && <Badge variant="outline" className="rounded-full font-normal">{j.category}</Badge>}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          {j.location && <span className="flex items-center gap-1.5"><MapPin size={15} /> {j.location}</span>}
          {salaryLabel(j) && <span className="flex items-center gap-1.5 font-medium text-secondary">{salaryLabel(j)}</span>}
          <span className="flex items-center gap-1.5"><Clock size={15} /> {timeago(j.created_at)}</span>
          <span className="flex items-center gap-1.5"><Eye size={15} /> {j.views} views</span>
        </div>

        {j.skills?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {j.skills.map((s) => <Badge key={s} variant="secondary" className="rounded-full font-normal">{s}</Badge>)}
          </div>
        )}

        <div className="mt-6 whitespace-pre-wrap leading-relaxed text-foreground/90" data-testid="job-description">{j.description}</div>

        {user && !j.am_poster && (
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl bg-accent/40 p-4">
            <UserPlus size={20} className="text-secondary" />
            <span className="text-sm">Know someone who'd be perfect for this role?</span>
            <Button variant="outline" size="sm" className="ml-auto gap-1.5 rounded-full" onClick={() => setShowRefer(true)} data-testid="refer-connection-btn"><UserPlus size={15} /> Refer a connection</Button>
          </div>
        )}

        {/* Poster */}
        <div className="mt-8 flex items-center gap-3 border-t border-border/60 pt-5">
          <Avatar className="h-10 w-10"><AvatarImage src={j.poster.avatar ? fileUrl(j.poster.avatar) : undefined} /><AvatarFallback>{(j.poster.name || "M")[0]}</AvatarFallback></Avatar>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Posted by</p>
            <div className="flex items-center gap-1">
              {j.poster.slug ? <Link to={`/u/${j.poster.slug}`} className="text-sm font-semibold hover:underline">{j.poster.name}</Link> : <span className="text-sm font-semibold">{j.poster.name}</span>}
              <SocialPulseBadge verified={j.poster.socialpulse_verified} showLabel={false} />
            </div>
          </div>
        </div>

        {/* Poster controls */}
        {j.am_poster && (
          <div className="mt-5 flex flex-wrap gap-2 border-t border-border/60 pt-5" data-testid="poster-controls">
            <Button variant="outline" className="gap-2 rounded-full" onClick={() => setShowApplicants(true)} data-testid="view-applicants-btn"><Users size={16} /> Applicants ({j.application_count})</Button>
            <Button variant="outline" className="gap-2 rounded-full" onClick={() => setShowEdit(true)} data-testid="edit-job-btn"><PencilSimple size={16} /> Edit</Button>
            <Button variant="outline" className="rounded-full" onClick={toggleClose} data-testid="toggle-close-btn">{j.status === "open" ? "Close job" : "Reopen"}</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" className="gap-2 rounded-full text-destructive" data-testid="delete-job-btn"><Trash size={16} /> Delete</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete this job?</AlertDialogTitle><AlertDialogDescription>This removes the posting and all applications. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} data-testid="confirm-delete-job">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </Card>

      {/* Apply dialog */}
      <Dialog open={showApply} onOpenChange={setShowApply}>
        <DialogContent data-testid="apply-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">Apply to {j.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={5} placeholder="Add a short note for the poster (optional)" data-testid="apply-note" />
            <Input value={resumeUrl} onChange={(e) => setResumeUrl(e.target.value)} placeholder="Link to your resume (optional)" data-testid="apply-resume" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setShowApply(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={apply} disabled={applying} data-testid="apply-submit">{applying ? "Sending…" : "Send application"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showEdit && <JobFormDialog open={showEdit} onOpenChange={setShowEdit} initial={j} onSaved={() => load()} />}
      <ApplicantsDialog jobId={id} open={showApplicants} onOpenChange={setShowApplicants} />
      <ReferDialog jobId={id} jobTitle={j.title} open={showRefer} onOpenChange={setShowRefer} />
    </main>
  );
}
